import { ProblemError, isProblemError } from "../models/problemError";
import { errorDecision } from "../constants/errorDecisions";

export type FailureDisplay = {
  message: string;
  // The value the payer is told to quote to support. Present whenever the
  // request carried one, which is every request the portal makes (D-17).
  reference?: string;
};

const GENERIC_KEY = "errors.unknown_error";

// CPS's own codes and the platform catalogue's numeric ones.
const CODE_SHAPE = /^(CPS-\d{3}-\d{3}|\d+)$/;

// A code is unique across the whole catalogue, so it means the same thing
// whichever journey surfaced it. The school-fee namespace is checked as well
// as the general one so its already-reviewed copy keeps winning for the codes
// it covers, without every other journey having to restate them.
const codeKeys = (code: string) => [
  `errors.${code}`,
  `schoolfee.errors.${code}`,
];

// i18next hands the key back when nothing is registered for it, so a bare
// truthiness check would render "schoolfee.errors.CPS-409-042" to a payer.
const copyFor = (
  translate: (key: string) => string | undefined,
  key: string
): string | undefined => {
  const value = translate(key);
  return value && value !== key ? value : undefined;
};

const firstCopy = (
  translate: (key: string) => string | undefined,
  keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = copyFor(translate, key);
    if (value) return value;
  }
  return undefined;
};

// Narrowed structurally rather than against the transport error class: utils
// sits below api in the dependency law, and the shape is all this needs.
// The legacy envelope names its code differently and in two places. Both are
// read because a decision is keyed by whichever one arrived; the mapped code
// is preferred because that is the one the catalogue is written against.
const legacyCode = (body: unknown): string | undefined => {
  if (typeof body !== "object" || body === null) return undefined;

  const candidate = body as {
    mappedErrorCode?: unknown;
    providerErrorCode?: unknown;
    errorCode?: unknown;
  };

  for (const value of [
    candidate.mappedErrorCode,
    candidate.errorCode,
    candidate.providerErrorCode,
  ]) {
    if (typeof value === "string" && value !== "") return value;
  }

  return undefined;
};

const failureShape = (
  error: unknown
): { problem?: ProblemError; reference?: string; legacy?: unknown } => {
  if (typeof error !== "object" || error === null) return {};

  const candidate = error as {
    problem?: unknown;
    correlationId?: unknown;
    body?: unknown;
  };

  return {
    problem: isProblemError(candidate.problem) ? candidate.problem : undefined,
    reference:
      typeof candidate.correlationId === "string"
        ? candidate.correlationId
        : undefined,
    // Either the failure wraps the body -- an onError handler holds the
    // transport error -- or it IS the body, because an onSuccess handler is
    // given the envelope directly. Both shapes reach this resolver.
    legacy: candidate.body ?? error,
  };
};

const serverText = (problem: ProblemError | undefined, language: string) => {
  if (!problem) return undefined;

  const preferred = language.toLowerCase().startsWith("fr")
    ? problem.message_fr || problem.message
    : problem.message || problem.message_fr;

  // An envelope carrying empty strings must reach the generic copy below, and
  // `??` would accept "" as an answer.
  return preferred || undefined;
};

// Some providers (observed on 703108) put a JSON-serialised diagnostics
// envelope -- raw/devMsg/providerErrorCode, the exact fields D-23 bans --
// into customerMsg as a STRING, instead of the approved sentence. A bare
// truthiness or emptiness check can't catch that; it has to be parsed and
// shape-checked. Human prose never round-trips through JSON.parse as an
// object or array, so this is a safe way to tell the two apart.
const isSerialisedDiagnostics = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return false;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
};

// CORE sends customerMsg as language-tagged entries; CPS currently narrows it
// to a single string. Stored rows keep whichever shape they were written with
// and InstrumentErr is a serialised map with no migration between them, so
// both shapes have to work permanently -- not only until 16-09 widens the
// inbound contract. Called with a string, `.find` would throw inside the error
// handler.
const legacyText = (body: unknown, language: string): string | undefined => {
  if (typeof body !== "object" || body === null) return undefined;

  const { customerMsg } = body as { customerMsg?: unknown };

  if (typeof customerMsg === "string") {
    const trimmed = customerMsg.trim();
    return trimmed && !isSerialisedDiagnostics(trimmed) ? trimmed : undefined;
  }

  if (!Array.isArray(customerMsg)) return undefined;

  const entries = customerMsg.filter(
    (entry): entry is { language: string; content: string } =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as { language?: unknown }).language === "string" &&
      typeof (entry as { content?: unknown }).content === "string"
  );

  // Same preference order as the server text above: the requested language
  // first, then whatever else the envelope carries, because the other locale
  // is still better for a payer than the generic string.
  const preferred = entries.find(
    entry => entry.language.toLowerCase() === language.toLowerCase()
  );

  return (preferred ?? entries[0])?.content.trim() || undefined;
};

// One entry point for turning a failure into words a payer can read. It
// dispatches on the envelope it actually receives, because the backend is
// forbidden from unifying the two: a route serving the legacy envelope keeps
// serving it, always, so a client has to handle both shapes permanently.
//
// D-22: the portal's own copy wins for a code it knows, so the wording matches
// the screen around it; the server's text is the fallback for a code the
// portal has never heard of, which is better than showing nothing.
// D-23: nothing here ever reads `details[]`, the developer message or the
// raw response body.
export const getFailureDisplay = (
  error: unknown,
  translate: (key: string) => string | undefined,
  language: string
): FailureDisplay => {
  const { problem, reference, legacy } = failureShape(error);

  // Keyed by whichever envelope arrived: provider failures reach the receipt
  // page in the legacy one, and an OVERRIDE must win there too.
  const code = problem?.code ?? legacyCode(legacy);

  // Only a catalogue-shaped code is looked up: i18next reads ":" as a
  // namespace separator and resolves non-code keys, so "MTN:123" or "title"
  // would otherwise render a fragment no one reviewed.
  const portalCopy =
    code && CODE_SHAPE.test(code)
      ? firstCopy(translate, codeKeys(code))
      : undefined;

  // OQ-6 / R-6: the portal never renders an un-reviewed upstream message
  // verbatim. Upstream prose is shown only where a decision approved it, so
  // pass-through is a reviewed state and not the fallback it used to be --
  // blanket pass-through can breach the anti-tipping-off rule.
  const approvedVerbatim = errorDecision(code)?.state === "PASSTHROUGH";

  // OQ-4: anything un-reviewed falls to the generic message plus the
  // reference. Every waived code therefore reads generically -- that is the
  // visible cost of the review backlog, and the reason for burning it down.
  const message =
    portalCopy ??
    (approvedVerbatim ? serverText(problem, language) : undefined) ??
    (approvedVerbatim ? legacyText(legacy, language) : undefined) ??
    copyFor(translate, GENERIC_KEY) ??
    GENERIC_KEY;

  return { message, reference };
};

export default getFailureDisplay;
